import { c as _c } from "react/compiler-runtime";
import { addDays, endOfMonth, format, getDay, isAfter, isEqual, startOfDay, startOfMonth } from 'date-fns';
import { useMemo } from 'react';
import type { CalendarEvent } from '@/app/(marketing-site)/bezplatna-konsultacja/_components/CalendarEvent';
import { WEEK_DAYS } from '../_components/HourSelector';
interface UseMergedSelectedEventsProps {
  selectedEventsMap: Map<string, CalendarEvent[]>;
  selectedHoursByDay: Record<WEEK_DAYS, string[]>;
  excludedRecurringEvents: Set<string>;
}
export function useMergedSelectedEvents(t0) {
  const $ = _c(6);
  const {
    selectedEventsMap,
    selectedHoursByDay,
    excludedRecurringEvents
  } = t0;
  let currentMonthStart;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const today = new Date();
    currentMonthStart = startOfMonth(today);
    t1 = endOfMonth(today);
    $[0] = currentMonthStart;
    $[1] = t1;
  } else {
    currentMonthStart = $[0];
    t1 = $[1];
  }
  const currentMonthEnd = t1;
  let result;
  if ($[2] !== excludedRecurringEvents || $[3] !== selectedEventsMap || $[4] !== selectedHoursByDay) {
    const baseEvents = new Map();
    for (const [date, events] of selectedEventsMap.entries()) {
      const filtered = events.filter(event => {
        if (event.isRecurring && event.id) {
          const hour = format(event.startTime, "HH:mm");
          const exclusionKey = `${date}-${hour}`;
          if (excludedRecurringEvents.has(exclusionKey)) {
            return false;
          }
          const dayOfWeek = getDay(event.startTime);
          const dayName = Object.values(WEEK_DAYS)[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
          const isInSelectedHours = selectedHoursByDay[dayName]?.includes(hour);
          if (!isInSelectedHours) {
            return false;
          }
        }
        return true;
      });
      if (filtered.length > 0) {
        baseEvents.set(date, filtered);
      }
    }
    result = baseEvents;
    const today_0 = startOfDay(new Date());
    let currentDate = currentMonthStart;
    while (!isAfter(currentDate, currentMonthEnd)) {
      if (currentDate < today_0) {
        currentDate = addDays(currentDate, 1);
        continue;
      }
      const dayOfWeek_0 = getDay(currentDate);
      const dayName_0 = Object.values(WEEK_DAYS)[dayOfWeek_0 === 0 ? 6 : dayOfWeek_0 - 1];
      const hours = selectedHoursByDay[dayName_0];
      if (hours.length > 0) {
        const formattedDate = format(currentDate, "yyyy.MM.dd");
        const existing = result.get(formattedDate) ?? [];
        const matching = hours.map(hour_0 => {
          const exclusionKey_0 = `${formattedDate}-${hour_0}`;
          if (excludedRecurringEvents.has(exclusionKey_0)) {
            return null;
          }
          const [h, m] = hour_0.split(":").map(Number);
          const date_0 = new Date(currentDate);
          date_0.setHours(h, m, 0, 0);
          if (isEqual(currentDate, today_0) && date_0 < new Date()) {
            return null;
          }
          const existingEventFromDb = existing.find(e => isEqual(e.startTime, date_0) && e.isRecurring);
          return {
            id: existingEventFromDb?.id,
            startTime: date_0,
            isBooked: false,
            isRecurring: true
          } as CalendarEvent;
        }).filter(Boolean) as CalendarEvent[];
        if (matching.length > 0) {
          result.set(formattedDate, [...existing, ...matching.filter(m_0 => !existing.some(e_0 => isEqual(e_0.startTime, m_0.startTime)))]);
        }
      }
      currentDate = addDays(currentDate, 1);
    }
    $[2] = excludedRecurringEvents;
    $[3] = selectedEventsMap;
    $[4] = selectedHoursByDay;
    $[5] = result;
  } else {
    result = $[5];
  }
  return result;
}